/**
 * Custom hook for managing game timer
 * Handles timer activation and decrementing time remaining every second
 */

import { useState, useEffect } from 'react';
import { GameState } from '../WordDriftUtils';
import { playSound, SharedSoundEffect } from '@mental-mint/shared-ui';

interface UseGameTimerReturn {
  isTimerActive: boolean;
  startTimer: () => void;
  deductTime: (seconds: number, updateAfterDeduction?: (state: GameState) => GameState) => void;
}

export function useGameTimer(
  gameState: GameState | null,
  setGameState: React.Dispatch<React.SetStateAction<GameState | null>>
): UseGameTimerReturn {
  const [isTimerActive, setIsTimerActive] = useState(false);

  // Timer: Decrement time remaining every second (starts when intro is dismissed)
  useEffect(() => {
    if (!gameState || gameState.isComplete || !isTimerActive) return;

    const interval = setInterval(() => {
      setGameState(prev => {
        if (!prev || prev.isComplete) return prev;
        
        // Check if time has run out
        if (prev.timeLeftSeconds <= 1) {
          // Play defeat sound when time runs out
          playSound(SharedSoundEffect.DEFEAT);
          
          return {
            ...prev,
            timeLeftSeconds: 0,
            isComplete: true,
            isWon: false,
          };
        }

        // Decrement by 1 second
        return {
          ...prev,
          timeLeftSeconds: prev.timeLeftSeconds - 1,
        };
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [gameState?.isComplete, isTimerActive, setGameState]);

  const startTimer = () => {
    setIsTimerActive(true);
  };

  // Deduct time when hint is used
  const deductTime = (seconds: number, updateAfterDeduction?: (state: GameState) => GameState) => {
    setGameState(prev => {
      if (!prev) return prev;
      
      const newTimeLeft = Math.max(0, prev.timeLeftSeconds - seconds);
      
      // Check if this deduction causes time to run out
      const isTimeLimitReached = newTimeLeft <= 0;
      
      // Play defeat sound if time runs out due to hint deduction
      if (isTimeLimitReached && !prev.isComplete) {
        playSound(SharedSoundEffect.DEFEAT);
      }
      
      const baseState = {
        ...prev,
        timeLeftSeconds: newTimeLeft,
        isComplete: isTimeLimitReached ? true : prev.isComplete,
        isWon: isTimeLimitReached ? false : prev.isWon,
      };

      return updateAfterDeduction ? updateAfterDeduction(baseState) : baseState;
    });
  };

  return {
    isTimerActive,
    startTimer,
    deductTime,
  };
}
